import React from 'react';
import { Platform } from 'react-native';
import {
  TransparentModal,
  useBreakpointHelpers,
  useNoticeBannersStackContext,
  useWebKeyListener,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { useLastInteractedStore } from '@order/LastInteractedStore';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { BagContents } from '../../components/Bag/Bag.contents';
import { useNavigateBack } from '../../navigation';
import type { BagScreenProps } from '../../navigation/AppNavigation.props';

export const BagScreen = ({ navigation }: BagScreenProps) => {
  const isLoggedIn = useIsLoggedIn();
  const { startSignIn } = useAuthFlow();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const lastInteractedStore = useLastInteractedStore();
  const { track } = useTelemetry();

  // ––– HANDLERS ––––––––––––––––––––––––––––––––––––––––––––––––

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );

  // – Navigate back

  const handleNavigateBack = useNavigateBack();
  const navigateBack = React.useCallback(() => {
    track('modal.close');
    handleNavigateBack();
  }, [track, handleNavigateBack]);

  // – Navigate to Checkout (on pressing 'Check out' and successfully applying rewards)

  const navigateToCheckout = React.useCallback(() => {
    if (isLoggedIn) {
      navigation.navigate('Checkout');

      return;
    }

    if (shouldUseAuthFlowRework) {
      navigation.navigate('Auth', {
        screen: 'Login',
        params: { redirect: 'checkout' },
      });

      return;
    }

    startSignIn('checkout');
  }, [isLoggedIn, shouldUseAuthFlowRework, startSignIn, navigation]);

  // – On press 'View Menu'

  const navigateToMenu = React.useCallback(() => {
    const { addressId, restaurantSlug } = lastInteractedStore ?? {};

    if (addressId) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'DeliveryMenu',
          params: { addressId },
        },
      });
    } else if (restaurantSlug) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Menu',
          params: { restaurantSlug },
        },
      });
    } else {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Locations',
        },
      });
    }
  }, [navigation, lastInteractedStore]);

  const dismissAndNavigateToMenu = React.useCallback(() => {
    if (Platform.OS === 'web') {
      // Workaround for a bug in the web implementation that was causing the address bar to display
      // an incorrect URL when navigating from a modal to a screen contained in a stack nested within a tab.
      // It requires us to first dismiss the modal, then navigate after a timeout,
      // otherwise the calls would be debounced.
      navigation.getParent()?.goBack();

      setTimeout(() => {
        navigateToMenu();
      });
    } else {
      navigateToMenu();
    }
  }, [navigation, navigateToMenu]);

  // – Can change location? If signed out, start sign-in and redirect to Locations

  const canChangeLocation = React.useCallback(() => {
    if (isLoggedIn) return true;

    const text = t('bag.reorder.error-logged-out');

    addNoticeBanner({ text, palette: 'neutral' });

    if (shouldUseAuthFlowRework) {
      navigation.navigate('Auth', {
        screen: 'Login',
        params: { redirect: 'locations' },
      });

      return false;
    }

    startSignIn('locations');

    return false;
  }, [
    isLoggedIn,
    t,
    addNoticeBanner,
    shouldUseAuthFlowRework,
    startSignIn,
    navigation,
  ]);

  useWebKeyListener('Escape', navigateBack);

  const { isMobile } = useBreakpointHelpers();

  return (
    <TransparentModal
      size={isMobile ? 'full' : 'drawer'}
      onClose={navigateBack}
      mobileEdges={['right', 'left']}
    >
      <BagContents
        navigateToMenu={dismissAndNavigateToMenu}
        navigateToCheckout={navigateToCheckout}
        canChangeLocation={canChangeLocation}
      />
    </TransparentModal>
  );
};
